/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { OrdersDetailsFromState, OrderItemsFromState } from '@/types/reduxStore/orders';
import actionTypes from '../../types/action';
import { type Action } from '@/types';

export const initialState: OrdersDetailsFromState = {
  readyStatus: actionTypes.ORDER_DETAILS_INVALID,
  err: null,
  orderItems: [],
  orderSummary: null,
  paymentInfoList: [],
  cartData: {},
  rewardsData: null,
  cartVerifyData: {},
  cartItemError: {},
  orderDetailsError: null,
  loadingSkuNumber: null,
  quantity: '',
  rewardsCreditEligible: undefined,
  orderStatus: '',
};

/**
 * Transform order data to ensure selectedFulfillmentType is populated at lineItem level
 *
 * ISSUE: Backend API inconsistency across endpoints:
 * - GET /order/v4/details includes selectedFulfillmentType at lineItem level
 * - PATCH /order/payment (gift card/rewards) does NOT include selectedFulfillmentType at lineItem level
 *
 * Without this transformation, applying a gift card causes selectedFulfillmentType to become
 * undefined, which breaks VIN/Mileage submission that depends on this field.
 *
 * SOLUTION:
 * - Always preserve selectedFulfillmentType from order details API
 * - When missing from payment API responses, restore from stored values in state
 *
 * @param orders - Array of orders from API response
 * @param existingOrders - Existing orders in state to restore selectedFulfillmentType from
 * @returns Transformed orders with selectedFulfillmentType populated
 */
const transformOrdersWithFulfillmentType = (
  orders: OrderItemsFromState,
  existingOrders: OrderItemsFromState = [],
): OrderItemsFromState => {
  // Create a lookup map of existing selectedFulfillmentType values by SKU
  const fulfillmentTypeMap = new Map<string, number>();

  existingOrders.forEach((order) => {
    order.shipmentInfoList.forEach((shipment) => {
      shipment.lineItemList.forEach((lineItem) => {
        if (lineItem.selectedFulfillmentType != null && lineItem.productInfo.skuId) {
          fulfillmentTypeMap.set(lineItem.productInfo.skuId, lineItem.selectedFulfillmentType);
        }
      });
    });
  });

  return orders.map((order) => ({
    ...order,
    shipmentInfoList: order.shipmentInfoList.map((shipment) => ({
      ...shipment,
      lineItemList: shipment.lineItemList.map((lineItem) => {
        // Use existing selectedFulfillmentType if present, otherwise restore from stored values
        const selectedFulfillmentType =
          lineItem.selectedFulfillmentType ??
          fulfillmentTypeMap.get(lineItem.productInfo.skuId) ??
          null;

        return {
          ...lineItem,
          selectedFulfillmentType,
        };
      }),
    })),
  }));
};

const {
  ORDER_DETAILS_FAILURE,
  ORDER_DETAILS_REQUESTING,
  ORDER_DETAILS_SUCCESS,
  UPDATE_ORDERITEM_REQUESTING,
  UPDATE_ORDERITEM_SUCCESS,
  UPDATE_ORDERITEM_FAILURE,
  UPDATE_ITEM_QUANTITY_SUCCESS,
  ORDER_DETAILS_CLEAR,
  CLEAR_ORDER_DETAILS,
  SET_CHECKOUT_BUTTON,
  SET_LOADING_FOR_PRODUCT,
  SET_ORDER_STATUS,
} = actionTypes;

const details = (
  state: OrdersDetailsFromState = initialState,
  action: Action,
): OrdersDetailsFromState => {
  switch (action.type) {
    case ORDER_DETAILS_REQUESTING:
      return { ...state, readyStatus: ORDER_DETAILS_REQUESTING };

    case ORDER_DETAILS_SUCCESS:
      return {
        ...state,
        readyStatus: ORDER_DETAILS_SUCCESS,
        orderItems: transformOrdersWithFulfillmentType(action.data.orders ?? []),
        orderSummary: action.data.grandOrderPriceInfo,
        paymentInfoList: action.data.grandPaymentAccountList,
        rewardsCreditEligible: action.data.rewardsCreditEligible,
      };

    case ORDER_DETAILS_FAILURE:
      return {
        ...state,
        readyStatus: ORDER_DETAILS_FAILURE,
        err: action.err,
      };

    case UPDATE_ORDERITEM_REQUESTING:
      return { ...state, readyStatus: UPDATE_ORDERITEM_REQUESTING };

    case UPDATE_ORDERITEM_SUCCESS:
      return {
        ...state,
        readyStatus: UPDATE_ORDERITEM_SUCCESS,
        orderItems: transformOrdersWithFulfillmentType(action.data.orders ?? [], state.orderItems),
        orderSummary: action.data.grandOrderPriceInfo,
        paymentInfoList: action.data.grandPaymentAccountList,
        orderDetailsError: null,
      };

    case UPDATE_ITEM_QUANTITY_SUCCESS:
      return {
        ...state,
        readyStatus: UPDATE_ITEM_QUANTITY_SUCCESS,
        skuId: action.data.skuId,
        quantity: action.data.quantity,
      };

    case UPDATE_ORDERITEM_FAILURE:
      return {
        ...state,
        readyStatus: UPDATE_ORDERITEM_FAILURE,
        orderDetailsError: action.err,
      };

    case ORDER_DETAILS_CLEAR:
      return {
        ...state,
        readyStatus: actionTypes.ORDER_DETAILS_INVALID,
        err: null,
        orderItems: [],
        orderSummary: null,
        paymentInfoList: [],
      };

    case CLEAR_ORDER_DETAILS:
      return initialState;

    case SET_CHECKOUT_BUTTON:
      return { ...state, enableCheckoutButton: action.setCheckoutButton };

    case SET_ORDER_STATUS:
      return { ...state, orderStatus: action.orderStatus };

    case SET_LOADING_FOR_PRODUCT:
      return { ...state, loadingSkuNumber: action.skuId };

    default:
      return state;
  }
};

export default details;
